import { useHealthCheck } from '@/hooks/useHealthCheck';
import { useAssistantStore } from '@/stores/useAssistantStore';
import { Activity, Wifi, WifiOff, Server, Mic, Volume2, Cpu, Radio } from 'lucide-react';

export function StatusPanel() {
  const { health, connected } = useHealthCheck();
  const { apiBase, localAudioBase } = useAssistantStore();

  const subsystems = health?.subsystems || {};

  const statusColor = (status: string | undefined) => {
    const normalized = status?.toLowerCase();
    if (!status) return 'bg-disabled';
    if (normalized === 'live' || normalized === 'wired' || normalized === 'open') return 'bg-neon';
    if (normalized === 'degraded') return 'bg-gold';
    return 'bg-fire';
  };

  const statusGlow = (status: string | undefined) => {
    const normalized = status?.toLowerCase();
    if (!status) return '';
    if (normalized === 'live' || normalized === 'wired' || normalized === 'open') return 'shadow-[0_0_8px_rgba(57,255,20,0.6)]';
    if (normalized === 'degraded') return 'shadow-[0_0_8px_rgba(255,215,0,0.6)]';
    return 'shadow-[0_0_8px_rgba(255,51,102,0.6)]';
  };

  return (
    <div className="bg-void/95 border border-border-subtle rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle bg-surface/30">
        <Activity size={14} className={connected ? 'text-neon' : 'text-fire'} />
        <span className="text-label">SYSTEM STATUS</span>
        <div className="ml-auto flex items-center gap-1.5">
          {connected ? <Wifi size={12} className="text-neon" /> : <WifiOff size={12} className="text-fire" />}
          <span className="text-mono-sm text-tertiary">CONTROL: {apiBase}</span>
        </div>
      </div>

      <div className="p-3 space-y-2">
        {/* Health Summary */}
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${statusColor(connected ? 'live' : 'absent')} ${statusGlow(connected ? 'live' : 'absent')}`} />
          <span className="text-body-sm text-primary">
            {health?.status?.toUpperCase() || 'OFFLINE'}
          </span>
          <span className="text-mono-sm text-tertiary ml-auto">
            {health?.version || '—'}
          </span>
        </div>

        {/* Subsystems Grid */}
        <div className="grid grid-cols-2 gap-2 mt-2">
          <SubsystemRow icon={Mic} label="STT" status={subsystems.stt || subsystems.local_stt} />
          <SubsystemRow icon={Volume2} label="TTS" status={subsystems.tts || subsystems.local_tts} />
          <SubsystemRow icon={Cpu} label="DSP" status={subsystems.dsp} />
          <SubsystemRow icon={Server} label="DAW" status={subsystems.daw} />
          <SubsystemRow icon={Radio} label="FIONA" status={subsystems.fiona_channel} />
          <SubsystemRow icon={Radio} label="AGENT" status={subsystems.agent_channel} />
        </div>

        <div className="pt-1 text-[10px] text-tertiary truncate" title={localAudioBase}>
          LOCAL AUDIO: {localAudioBase}
        </div>

        {/* Adapters */}
        {health?.adapters && health.adapters.length > 0 && (
          <div className="mt-2 pt-2 border-t border-border-subtle">
            <span className="text-label text-tertiary">ADAPTERS</span>
            <div className="mt-1 space-y-1">
              {health.adapters.map((a) => (
                <div key={a.id} className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${statusColor(a.status)}`} />
                  <span className="text-mono-sm text-secondary">{a.id}</span>
                  <span className="text-mono-sm text-tertiary ml-auto">
                    {new Date(a.last_handshake).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Uptime */}
        {health?.uptime_seconds && (
          <div className="mt-2 pt-2 border-t border-border-subtle flex items-center gap-2">
            <span className="text-label text-tertiary">UPTIME</span>
            <span className="text-mono-sm text-secondary ml-auto">
              {Math.floor(health.uptime_seconds / 3600)}h {Math.floor((health.uptime_seconds % 3600) / 60)}m
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function SubsystemRow({ icon: Icon, label, status }: { icon: typeof Activity; label: string; status?: string }) {
  const normalized = status?.toLowerCase();
  const color = !status ? 'bg-disabled' : normalized === 'live' || normalized === 'wired' || normalized === 'open' ? 'bg-neon' : normalized === 'degraded' ? 'bg-gold' : 'bg-fire';
  const glow = !status ? '' : normalized === 'live' || normalized === 'wired' || normalized === 'open' ? 'shadow-[0_0_6px_rgba(57,255,20,0.5)]' : normalized === 'degraded' ? 'shadow-[0_0_6px_rgba(255,215,0,0.5)]' : 'shadow-[0_0_6px_rgba(255,51,102,0.5)]';

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-surface/30">
      <Icon size={12} className="text-tertiary" />
      <span className="text-mono-sm text-secondary">{label}</span>
      <div className="ml-auto flex items-center gap-1.5">
        <div className={`w-2 h-2 rounded-full ${color} ${glow}`} />
        <span className="text-[10px] uppercase text-tertiary">{status?.toUpperCase() || 'ABSENT'}</span>
      </div>
    </div>
  );
}
